import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Play, ArrowRight, X, Music, Gift, Cake } from 'lucide-react';
import { apiFetch } from '../App';

// Image assets
const BACKGROUND_IMAGE = "/assets/migrated/background.png";
const LOGO_IMAGE = "/assets/brand/logo-inline.png";

// Step images from stock
const STEP1_IMAGE = "/assets/migrated/step-create.jpg";
const STEP2_IMAGE = "/assets/migrated/step-listen.jpg";
const STEP3_IMAGE = "/assets/migrated/step-gift.jpg";

// Animated Stars Component
const AnimatedStars = () => {
  // Generate random stars
  const stars = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: Math.random() * 3 + 1,
    duration: Math.random() * 3 + 2,
    delay: Math.random() * 5,
    driftX: (Math.random() - 0.5) * 100,
    driftY: (Math.random() - 0.5) * 50,
  }));

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {stars.map((star) => (
        <div
          key={star.id}
          className="absolute rounded-full bg-white"
          style={{
            left: `${star.left}%`,
            top: `${star.top}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            animation: `twinkle ${star.duration}s ease-in-out infinite, drift ${star.duration * 10}s ease-in-out infinite`,
            animationDelay: `${star.delay}s`,
            '--drift-x': `${star.driftX}px`,
            '--drift-y': `${star.driftY}px`,
          }}
        />
      ))}
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes drift {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(var(--drift-x), var(--drift-y)); }
        }
      `}</style>
    </div>
  );
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAuth, setShowAuth] = useState(false);
  const [creditPackages, setCreditPackages] = useState([]);
  const [creditCosts, setCreditCosts] = useState({ lyric_generation: 5, album_cover: 3, song_unlock: 20 });
  
  // Share modal state
  const [shareModal, setShareModal] = useState(null); // { type: 'song' | 'gift', data: {...} }
  const [loadingShare, setLoadingShare] = useState(false);

  // Check for share params on load
  useEffect(() => {
    const shareType = searchParams.get('share');
    const shareId = searchParams.get('id');
    
    if (shareType && shareId) {
      loadShareData(shareType, shareId);
    }
  }, [searchParams]);
  
  const loadShareData = async (type, id) => {
    setLoadingShare(true);
    try {
      let endpoint = '';
      if (type === 'song') {
        endpoint = `/share/song/${id}/data`;
      } else if (type === 'gift') {
        // Gift endpoint is already public
        endpoint = `/gifts/${id}`;
      } else if (type === 'birthday') {
        endpoint = `/share/birthday/${id}/data`;
      }
      
      if (!endpoint) {
        setSearchParams({});
        return;
      }
      
      const { data, error } = await apiFetch(endpoint);
      if (data && !error) {
        // Normalize gift data structure
        if (type === 'gift' && data.gift) {
          setShareModal({ 
            type, 
            data: {
              ...data.gift,
              song: data.song,
              cover: data.cover
            }
          });
        } else {
          setShareModal({ type, data });
        }
      } else {
        // Clear params if not found
        setSearchParams({});
      }
    } catch (error) {
      console.error('Error loading share data:', error);
      setSearchParams({});
    } finally {
      setLoadingShare(false);
    }
  };
  
  const closeShareModal = () => {
    setShareModal(null);
    setSearchParams({});
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [packagesRes, costsRes] = await Promise.all([
          apiFetch('/credits/packages'),
          apiFetch('/credits/costs')
        ]);
        if (packagesRes.data) setCreditPackages(packagesRes.data);
        if (costsRes.data) setCreditCosts(costsRes.data);
      } catch (error) {
        console.error('Error fetching landing page data:', error);
      }
    };
    fetchData();
  }, []);

  const steps = [
    {
      number: 1,
      title: "Start with a thought",
      image: STEP1_IMAGE,
      description: [
        "Type a theme, story, or any words that come to mind.",
        "Pick a style and a mood.",
        "Select Generate Song."
      ]
    },
    {
      number: 2,
      title: "Listen and refine",
      image: STEP2_IMAGE,
      description: [
        "Our system will compose a full song",
        "for you to hear and tweak to your liking.",
        "Change lyrics, instruments, vocals, and more."
      ]
    },
    {
      number: 3,
      title: "Download and share",
      image: STEP3_IMAGE,
      description: [
        "Download your finished song as a",
        "professional-quality recording",
        "or send a free gift card to share",
        "a link to your song."
      ]
    }
  ];

  return (
    <div 
      className="min-h-screen text-white relative"
      style={{
        backgroundImage: `url('${BACKGROUND_IMAGE}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Animated Stars */}
      <AnimatedStars />
      
      {/* HERO SECTION */}
      <section className="text-center px-6 pt-16 pb-20 relative z-10">
        {/* LOGO */}
        <div className="flex justify-center mb-8">
          <img
            src={LOGO_IMAGE}
            alt="Wrapped In Music"
            className="h-11 md:h-16 w-auto"
            data-testid="hero-logo"
          />
        </div>

        {/* HEADLINE */}
        <h1 className="text-2xl md:text-4xl lg:text-5xl font-light mb-4 tracking-wide">
          Turn your ideas into songs in minutes — no experience needed.
        </h1>

        {/* SUBHEAD */}
        <p className="text-lg md:text-xl text-white/80 mb-4">
          Describe your song with as little as one word. Pick a style and mood. Get a song.
        </p>

        {/* SUPPORT LINE */}
        <p className="text-white/70 mb-10">
          Or select one of our 4 'Not sure where to start?' buttons for an instant song.
        </p>

        {/* BUTTONS */}
        <div className="flex justify-center gap-4 flex-wrap mb-6">
          <Button 
            onClick={() => navigate('/auth')}
            className="px-8 py-6 rounded-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-medium text-lg transition-all shadow-lg shadow-purple-500/30"
            data-testid="get-started-btn"
          >
            Create Your First Song <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          {/* Watch Demo button - uncomment when ready
          <Button 
            variant="outline"
            className="px-8 py-6 rounded-full border-2 border-white/40 bg-transparent hover:bg-white/10 text-white font-medium text-lg transition-all"
            data-testid="watch-demo-btn"
          >
            <Play className="mr-2 h-5 w-5" /> Watch Demo
          </Button>
          */}
        </div>
      </section>

      {/* DIVIDER */}
      <div className="w-full h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>

      {/* HOW IT WORKS SECTION */}
      <section className="px-6 py-20 text-center relative z-10">
        <h2 className="text-xl md:text-3xl mb-2 font-light">
          Create and share your custom song on social media in minutes
        </h2>
        <p className="text-sm text-white/80 mb-16">More advanced features are selectable to further enhance your songs and creative abilities as you progress.</p>

        <div className="grid md:grid-cols-3 gap-8 lg:gap-12 max-w-6xl mx-auto">
          {steps.map((step) => (
            <div key={step.number} className="flex flex-col items-center">
              {/* Step Number */}
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-xl font-bold mb-4 shadow-lg shadow-purple-500/30">
                {step.number}
              </div>

              {/* Step Title */}
              <h3 className="text-lg md:text-xl font-medium mb-6 text-white">
                {step.title}
              </h3>

              {/* Step Image */}
              <div className="relative mb-6 w-full max-w-[280px] aspect-square rounded-2xl overflow-hidden shadow-2xl shadow-purple-500/20">
                <img
                  src={step.image}
                  alt={step.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>

              {/* Step Description */}
              <div className="text-white/70 text-sm leading-relaxed">
                {step.description.map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* DIVIDER */}
      <div className="w-full h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>

      {/* PRICING SECTION */}
      <section className="px-6 py-20 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-4xl font-light mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-white/70">
            Choose the credit pack that works for you
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {creditPackages.map((plan, index) => (
            <div 
              key={plan.id || index}
              className={`relative rounded-2xl p-6 transition-all ${
                index === 1 
                  ? 'bg-gradient-to-br from-purple-600/30 to-purple-900/30 border-2 border-purple-500/50 scale-105 shadow-xl shadow-purple-500/20' 
                  : 'bg-white/5 border border-white/10 hover:bg-white/10'
              }`}
              data-testid={`pricing-card-${index}`}
            >
              {index === 1 && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-purple-500 rounded-full text-sm font-medium">
                  Most Popular
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                <div className="text-3xl font-bold text-white">
                  ${plan.price}
                </div>
                <div className="text-white/60 text-sm">
                  {plan.credits} credits
                </div>
              </div>

              <ul className="space-y-2 mb-6 text-sm text-white/70">
                <li>• {creditCosts.lyric_generation > 0 ? Math.floor(plan.credits / creditCosts.lyric_generation) : '∞'} lyric generations</li>
                <li>• Unlimited song generations</li>
                <li>• {creditCosts.song_unlock > 0 ? Math.floor(plan.credits / creditCosts.song_unlock) : '∞'} song unlocks ({creditCosts.song_unlock ?? 20} credits each)</li>
                <li>• {creditCosts.album_cover > 0 ? Math.floor(plan.credits / creditCosts.album_cover) : '∞'} album covers</li>
              </ul>

              <Button 
                onClick={() => navigate('/auth')}
                className={`w-full py-3 rounded-full font-medium transition-all ${
                  index === 1 
                    ? 'bg-purple-500 hover:bg-purple-400 text-white' 
                    : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                }`}
              >
                Get Started
              </Button>
            </div>
          ))}
        </div>

        <p className="text-center text-white/50 text-sm mt-8">
          All plans include free song generation. Pay only for what you keep.
        </p>
      </section>

      {/* SHARE MODAL OVERLAY */}
      {(shareModal || loadingShare) && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={closeShareModal}
        >
          <div 
            className="relative bg-gradient-to-br from-purple-900/90 to-slate-900/90 border border-purple-500/30 rounded-2xl max-w-lg w-full p-6 shadow-2xl shadow-purple-500/20"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={closeShareModal}
              className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
              data-testid="share-modal-close"
            >
              <X className="h-6 w-6" />
            </button>

            {loadingShare ? (
              <div className="text-center py-12">
                <div className="inline-block h-8 w-8 border-2 border-white/30 border-t-purple-500 rounded-full animate-spin mb-4"></div>
                <p className="text-white/70">Loading...</p>
              </div>
            ) : shareModal?.type === 'song' ? (
              /* SONG SHARE MODAL */
              <div className="text-center">
                {/* Song artwork */}
                <div className="relative w-48 h-48 mx-auto mb-6 rounded-xl overflow-hidden shadow-lg shadow-purple-500/30">
                  {shareModal.data.image_url ? (
                    <img 
                      src={shareModal.data.image_url} 
                      alt={shareModal.data.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-600 to-purple-900 flex items-center justify-center">
                      <Music className="h-16 w-16 text-white/50" />
                    </div>
                  )}
                </div>

                <h2 className="text-2xl font-bold text-white mb-2">
                  🎵 {shareModal.data.title || 'Untitled Song'}
                </h2>
                
                {shareModal.data.tags && (
                  <p className="text-white/70 text-sm mb-4 line-clamp-2">
                    {shareModal.data.tags}
                  </p>
                )}

                {/* Audio preview */}
                {shareModal.data.audio_url && (
                  <div className="mb-6">
                    <audio 
                      controls 
                      className="w-full rounded-lg"
                      data-testid="share-modal-audio"
                    >
                      <source src={shareModal.data.audio_url} type="audio/mpeg" />
                    </audio>
                  </div>
                )}

                <p className="text-white/60 text-sm mb-6">
                  Created with Wrapped In Music - AI-powered songwriting
                </p>

                <Button
                  onClick={() => navigate('/auth')}
                  className="w-full py-4 rounded-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-medium text-lg transition-all shadow-lg shadow-purple-500/30"
                  data-testid="share-modal-signup-btn"
                >
                  Create Your Own Song <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            ) : shareModal?.type === 'gift' ? (
              /* GIFT CARD SHARE MODAL */
              <div className="text-center">
                {/* Gift icon badge */}
                <div className="flex justify-center mb-4">
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-3 rounded-full shadow-lg shadow-amber-500/30">
                    <Gift className="h-8 w-8 text-white" />
                  </div>
                </div>

                {/* Cover/Song artwork */}
                <div className="relative w-48 h-48 mx-auto mb-4 rounded-xl overflow-hidden shadow-lg shadow-purple-500/30">
                  {shareModal.data.cover?.image_url ? (
                    <img 
                      src={shareModal.data.cover.image_url} 
                      alt="Gift cover"
                      className="w-full h-full object-cover"
                    />
                  ) : shareModal.data.song?.image_url ? (
                    <img 
                      src={shareModal.data.song.image_url} 
                      alt={shareModal.data.song.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-amber-600 to-purple-900 flex items-center justify-center">
                      <Gift className="h-16 w-16 text-white/50" />
                    </div>
                  )}
                </div>

                <h2 className="text-xl font-bold text-white mb-1">
                  🎁 A Musical Gift for {shareModal.data.recipient_name || 'You'}
                </h2>
                
                <h3 className="text-lg text-purple-300 mb-3">
                  🎵 {shareModal.data.song?.title || 'A Special Song'}
                </h3>
                
                {shareModal.data.message && (
                  <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
                    <p className="text-white/80 text-sm italic">
                      "{shareModal.data.message}"
                    </p>
                    {shareModal.data.sender_name && (
                      <p className="text-white/50 text-xs mt-2">
                        — {shareModal.data.sender_name}
                      </p>
                    )}
                  </div>
                )}

                {/* Audio preview */}
                {shareModal.data.song?.audio_url && (
                  <div className="mb-6">
                    <audio 
                      controls 
                      className="w-full rounded-lg"
                      data-testid="share-modal-gift-audio"
                    >
                      <source src={shareModal.data.song.audio_url} type="audio/mpeg" />
                    </audio>
                  </div>
                )}

                <p className="text-white/60 text-sm mb-6">
                  Send personalized song gifts to your loved ones
                </p>

                <Button
                  onClick={() => navigate('/auth')}
                  className="w-full py-4 rounded-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-medium text-lg transition-all shadow-lg shadow-purple-500/30"
                  data-testid="share-modal-gift-signup-btn"
                >
                  Create Your Own Gift <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            ) : shareModal?.type === 'birthday' ? (
              /* BIRTHDAY VIDEO SHARE MODAL */
              <div className="text-center">
                {/* Birthday icon badge */}
                <div className="flex justify-center mb-4">
                  <div className="bg-gradient-to-br from-pink-500 to-rose-600 p-3 rounded-full shadow-lg shadow-pink-500/30">
                    <Cake className="h-8 w-8 text-white" />
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-white mb-2">
                  🎂 Happy Birthday {shareModal.data.name || 'Someone Special'}!
                </h2>
                
                <p className="text-white/70 text-sm mb-4">
                  A personalized birthday song video created with AI
                </p>

                {/* Video preview */}
                {shareModal.data.video_url && (
                  <div className="mb-6 rounded-xl overflow-hidden shadow-lg">
                    <video 
                      controls 
                      className="w-full max-h-64"
                      poster={LOGO_IMAGE}
                      data-testid="share-modal-birthday-video"
                    >
                      <source src={shareModal.data.video_url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                )}

                <p className="text-white/60 text-sm mb-6">
                  Create personalized birthday songs for your loved ones!
                </p>

                <Button
                  onClick={() => navigate('/auth')}
                  className="w-full py-4 rounded-full bg-gradient-to-r from-pink-600 to-rose-500 hover:from-pink-500 hover:to-rose-400 text-white font-medium text-lg transition-all shadow-lg shadow-pink-500/30"
                  data-testid="share-modal-birthday-signup-btn"
                >
                  Create a Birthday Song <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
